// Long-form SEO guide content. Each guide renders via GuideArticlePage.
export const GUIDES = [
  {
    slug: 'track-coi-expiration-across-portfolio',
    title: 'How to Track COI Expiration Across a Property Portfolio',
    description:
      'A practical playbook for property managers to track Certificate of Insurance (COI) expiration dates across hundreds of vendors and multiple properties—without spreadsheets or missed lapses.',
    datePublished: '2026-07-01',
    readTime: '8 min read',
    tag: 'Compliance Playbook',
    excerpt:
      'Expired COIs are the #1 silent liability in vendor management. Here is how to catch every lapse before it becomes an uninsured claim.',
    sections: [
      {
        heading: 'Why COI expiration is the hardest part of vendor compliance',
        paragraphs: [
          "A Certificate of Insurance is only useful while it is active. The problem is that COIs expire on a rolling basis—every vendor's general liability, workers' compensation, and auto policies renew on different dates, and each renewal produces a new certificate that has to be collected, read, and verified all over again.",
          "For a property management company running even 200 vendors across a dozen properties, that can mean several hundred expiration dates to monitor. Miss one, and a vendor performs work on your property while uninsured. If something goes wrong, the claim can flow straight back to the owner and the management company.",
        ],
      },
      {
        heading: 'The spreadsheet method (and why it breaks)',
        paragraphs: [
          'Most teams start with a shared spreadsheet: one row per vendor, columns for each policy type and its expiration date, conditional formatting to turn cells red when a date is close. It works until it does not.',
        ],
        list: [
          'Manual data entry means a typo in a single date silently hides a lapse.',
          'Nobody owns the follow-up, so red cells pile up unaddressed.',
          'It captures the expiration date but not the coverage amounts, so an underinsured vendor still looks "green".',
          'It cannot enforce different requirements for different properties (a high-rise needs more coverage than a duplex).',
        ],
      },
      {
        heading: 'A system that actually scales',
        paragraphs: [
          'The durable fix is to treat COI tracking as a data pipeline, not a document folder. Four capabilities matter:',
        ],
        list: [
          'Automated extraction: when a COI arrives, OCR + AI should read the policy numbers, carriers, coverage limits, and—critically—the expiration dates, so nothing depends on manual typing.',
          'Requirement rules per property and vendor type: encode "commercial property requires $2M general liability, residential requires $1M" once, and let the system flag anything below the bar.',
          'A single expiration calendar: every policy across every vendor, sorted by soonest expiration, with a configurable warning window (30/60/90 days).',
          'Automated chasing: when a document is within the warning window, the system emails the vendor a reminder and a secure upload link—before it lapses, not after.',
        ],
      },
      {
        heading: 'How VendorSuite handles it',
        paragraphs: [
          'VendorSuite was built specifically for this problem. Vendors upload documents through a no-login magic link; the AI extraction pipeline (OCR plus LLM field understanding) reads every certificate and pulls the expiration dates automatically. Each date feeds a portfolio-wide compliance dashboard.',
          'When a policy enters the expiration window, the auto-chase engine sends escalating reminders to the vendor with a one-click upload link, and the dashboard flags the vendor as at-risk. A predictive engine can even begin chasing earlier for vendors with a history of late renewals. The result: expirations get handled before they become gaps, and you always know your true portfolio compliance rate.',
        ],
      },
      {
        heading: 'A simple checklist to get started',
        list: [
          'Inventory every vendor and the policy types each one is required to carry.',
          'Define coverage minimums per property type and per vendor type.',
          'Centralize all current COIs in one system that stores the expiration date as structured data.',
          'Set a warning window (start with 60 days) and assign an owner for follow-up—or automate it.',
          'Review the expiration calendar weekly and track your portfolio compliance rate over time.',
        ],
      },
    ],
  },
  {
    slug: 'vendor-compliance-checklist-property-managers',
    title: 'The Vendor Compliance Checklist for Property Management Companies',
    description:
      'A complete, step-by-step vendor compliance checklist for property management companies—what documents to collect, how to set requirements, and how to keep every vendor audit-ready year-round.',
    datePublished: '2026-07-05',
    readTime: '9 min read',
    tag: 'Checklist',
    excerpt:
      'Everything a PMC needs to collect, verify, and monitor to keep every vendor compliant and every property protected.',
    sections: [
      {
        heading: 'What "vendor compliance" actually means for a PMC',
        paragraphs: [
          'Vendor compliance is the ongoing process of making sure every third party who works on your properties carries the right insurance, licensing, and tax documentation—and keeps it current. It protects the owner, the management company, and residents from the financial and legal fallout of an uninsured or unlicensed vendor.',
          'Good compliance is not a one-time onboarding step. It is a continuous cycle: collect, verify, monitor, renew.',
        ],
      },
      {
        heading: 'Documents to collect from every vendor',
        list: [
          'Certificate of Insurance (COI) — general liability at minimum, with your entity named as additional insured.',
          "Workers' compensation certificate — for any vendor with employees performing on-site labor.",
          'Auto liability — for vendors who drive to or operate vehicles on the property.',
          'W-9 — for tax reporting and 1099 issuance.',
          'Trade licenses — for licensed trades (electrical, plumbing, HVAC).',
          'Umbrella / excess liability — for higher-risk or high-value properties.',
          'Signed service agreement or contract — defining scope, indemnification, and insurance requirements.',
        ],
      },
      {
        heading: 'Set requirements before you collect anything',
        paragraphs: [
          'The most common mistake is collecting documents without first defining what "compliant" means. Establish a three-layer requirement model:',
        ],
        list: [
          'Vendor-type defaults: a plumber needs a license and workers\' comp; a landscaper may not.',
          'Property-level overrides: a commercial high-rise requires higher coverage limits than a small residential building.',
          'Vendor-specific exceptions: a sole proprietor with no employees can be formally exempted from workers\' comp.',
        ],
      },
      {
        heading: 'Verify—do not just collect',
        paragraphs: [
          'Collecting a COI is not the same as verifying it. Every certificate should be checked for four things:',
        ],
        list: [
          'Is it current (not expired)?',
          'Does the coverage meet or exceed your requirement for that property?',
          'Is your entity listed as additional insured where required?',
          'Do the named insured and policy details match the vendor on file?',
        ],
      },
      {
        heading: 'Monitor and renew continuously',
        paragraphs: [
          'Policies expire year-round. Without a monitoring system, compliance quietly decays between onboarding and the next audit. Maintain a live expiration calendar, set a warning window, and automate reminders so vendors re-submit before coverage lapses.',
        ],
      },
      {
        heading: 'How VendorSuite automates the whole cycle',
        paragraphs: [
          'VendorSuite turns this checklist into an automated workflow. Vendors submit documents via a passwordless magic link; AI extraction reads and structures every field; a rules engine scores each vendor against the exact requirements for the properties they serve; and the auto-chase engine handles renewals before anything expires. Property managers get a single dashboard showing portfolio-wide compliance in real time, plus audit-ready records for every vendor.',
        ],
      },
    ],
  },
  {
    slug: 'additional-insured-requirements-property-managers',
    title: 'Additional Insured Requirements for Property Managers',
    description:
      'What "additional insured" means on a vendor COI, why it matters for property managers, and how to make sure every certificate actually names your entity correctly.',
    datePublished: '2026-07-25',
    readTime: '7 min read',
    tag: 'Insurance Explained',
    excerpt:
      'A COI is only protecting you if your entity is named as additional insured. Here is how to require it, read it, and verify it.',
    sections: [
      {
        heading: 'What "additional insured" actually means',
        paragraphs: [
          'An additional insured is a person or entity—other than the policyholder—who is extended coverage under a vendor\'s liability insurance policy. For a property manager, being named as an additional insured on a vendor\'s general liability policy means that if that vendor causes injury or damage on your property, the vendor\'s insurer defends and indemnifies you too, not just the vendor.',
          'Without additional insured status, a vendor can carry perfectly valid insurance that does nothing for you when a claim names the owner or management company.',
        ],
      },
      {
        heading: 'Why it matters more than the coverage limit',
        paragraphs: [
          'Property managers often focus on the dollar amount of coverage—$1M, $2M—and overlook whether they are actually covered by it. A $5M policy that does not name your entity as additional insured offers you no direct protection. The additional insured endorsement is what connects the vendor\'s coverage to your risk.',
        ],
        list: [
          'It shifts the defense and indemnity for covered claims to the vendor\'s insurer.',
          'It is typically required contractually before a vendor is allowed on site.',
          'It should be "primary and non-contributory" so the vendor\'s policy pays first, before yours.',
          'A waiver of subrogation is often required alongside it, preventing the insurer from later coming after you.',
        ],
      },
      {
        heading: 'How to read it on the certificate',
        paragraphs: [
          'On an ACORD certificate of insurance, additional insured status usually appears in the "Description of Operations" box or is indicated by a checkbox, and is backed by an endorsement form (commonly CG 20 10 and/or CG 20 37). Best practice is to require a copy of the actual endorsement, not just a mention on the certificate, because the certificate itself is informational and does not amend the policy.',
        ],
        list: [
          'Confirm your exact legal entity name is listed—not a trade name or a typo.',
          'Look for "primary and non-contributory" wording.',
          'Request the endorsement form number to confirm the coverage is real.',
          'Check that the additional insured status applies to both ongoing and completed operations where relevant.',
        ],
      },
      {
        heading: 'Set the requirement before the vendor starts',
        paragraphs: [
          'Additional insured status has to be contractually required up front—retrofitting it after an incident is impossible. Bake it into your vendor agreement and your insurance requirements, and make submitting a compliant certificate a condition of being approved to work.',
        ],
      },
      {
        heading: 'How VendorSuite verifies it automatically',
        paragraphs: [
          'VendorSuite lets you encode "must name [your entity] as additional insured, primary and non-contributory" as a requirement, then checks every incoming certificate against it. The AI extraction reads the named insured, coverage, and endorsement details; anything that does not meet the requirement is flagged before the vendor is marked compliant—so you never discover a missing endorsement at claim time.',
        ],
      },
    ],
  },
  {
    slug: 'coi-vs-w9-what-to-collect-from-vendors',
    title: 'COI vs. W-9: What Documents to Collect From Vendors',
    description:
      'A plain-English breakdown of the COI and the W-9—what each document is for, when you need it, and the full list of documents property managers should collect from every vendor.',
    datePublished: '2026-07-25',
    readTime: '6 min read',
    tag: 'Getting Started',
    excerpt:
      'The COI protects you from liability; the W-9 keeps you compliant with the IRS. Here is what each does and everything else to collect.',
    sections: [
      {
        heading: 'The short version',
        paragraphs: [
          'A Certificate of Insurance (COI) proves a vendor carries insurance and protects you from liability if something goes wrong. A W-9 captures a vendor\'s tax identification information so you can report payments to the IRS. They solve completely different problems—and you generally need both from every paid vendor.',
        ],
      },
      {
        heading: 'What a COI is for',
        paragraphs: [
          'The COI is a risk-management document. It summarizes a vendor\'s active insurance policies—general liability, workers\' compensation, auto—along with coverage limits and expiration dates. Property managers use it to confirm a vendor can cover the cost of injury or damage they cause, and (via additional insured status) to extend that protection to the owner and management company.',
        ],
        list: [
          'Collect it before the vendor performs any work.',
          'Verify coverage meets your requirement for that property.',
          'Confirm your entity is named as additional insured where required.',
          'Track the expiration date and re-collect on renewal.',
        ],
      },
      {
        heading: 'What a W-9 is for',
        paragraphs: [
          'The W-9 is a tax-compliance document. It records the vendor\'s legal name, business structure, and Taxpayer Identification Number (TIN or EIN). You need it to issue a Form 1099 for reportable payments and to avoid IRS backup-withholding penalties. Unlike a COI, a W-9 does not expire—but it should be refreshed if the vendor\'s name, entity type, or TIN changes.',
        ],
        list: [
          'Collect it during onboarding, before the first payment.',
          'Match the legal name and TIN to what the vendor invoices under.',
          'Re-collect if the business entity or ownership changes.',
        ],
      },
      {
        heading: 'The complete vendor document set',
        paragraphs: [
          'COI and W-9 are the two everyone knows, but a complete onboarding packet usually includes more depending on the vendor type and property:',
        ],
        list: [
          'Certificate of Insurance (general liability, and workers\' comp / auto as applicable).',
          'W-9 for tax reporting.',
          'Trade or business licenses for licensed trades (electrical, plumbing, HVAC).',
          'Additional insured and waiver-of-subrogation endorsements where required.',
          'A signed service agreement defining scope and insurance obligations.',
        ],
      },
      {
        heading: 'Collect and verify both in one flow',
        paragraphs: [
          'VendorSuite requests the full document set from each vendor through a single passwordless portal link, reads and structures every field automatically, and scores the vendor against your requirements—so the COI\'s coverage and the W-9\'s tax details are captured, verified, and monitored together instead of living in separate inboxes and folders.',
        ],
      },
    ],
  },
  {
    slug: 'vendor-insurance-requirements-by-property-type',
    title: 'Vendor Insurance Requirements by Property Type',
    description:
      'Insurance requirements should not be one-size-fits-all. Here is how to set vendor coverage requirements that scale with property type and risk—from single-family rentals to commercial high-rises.',
    datePublished: '2026-07-25',
    readTime: '8 min read',
    tag: 'Compliance Playbook',
    excerpt:
      'A high-rise and a duplex should not carry the same vendor requirements. Here is how to tier coverage by property type and risk.',
    sections: [
      {
        heading: 'Why requirements should vary by property',
        paragraphs: [
          'The risk a vendor introduces depends heavily on where they are working. A roofer on a 30-story commercial tower presents far greater exposure than a handyman at a single-family rental. Applying a flat insurance requirement across an entire portfolio either over-burdens low-risk vendors or, worse, under-protects your highest-value assets.',
          'The fix is a tiered requirement model: baseline coverage by vendor type, escalated by property type and risk profile.',
        ],
      },
      {
        heading: 'A practical tiering framework',
        list: [
          'Single-family / small residential: general liability commonly $1M per occurrence / $2M aggregate; workers\' comp where the vendor has employees.',
          'Mid-size multifamily: often $1M–$2M general liability, plus auto liability for vendors operating vehicles on site.',
          'Commercial / mixed-use: frequently $2M+ general liability, higher aggregates, and auto coverage.',
          'High-rise / high-value assets: commonly layered with umbrella/excess liability of $5M or more on top of the primary policy.',
        ],
        paragraphs: [
          'These are illustrative ranges, not legal advice—your actual requirements should follow your owners\' insurance counsel and lender or master-policy obligations.',
        ],
      },
      {
        heading: 'Layer requirements by vendor type on top',
        paragraphs: [
          'Property type sets the floor; vendor type adjusts it. Licensed trades and higher-hazard work (roofing, electrical, tree removal, pool service) warrant stricter coverage, licensing, and workers\' comp, while low-risk services may need less. The cleanest model is a three-layer system: a vendor-type default, a property-level override, and a per-vendor exception where justified.',
        ],
      },
      {
        heading: 'Do not forget the endorsements',
        paragraphs: [
          'Coverage limits are only half the requirement. For most property types you will also want your entity named as additional insured on a primary and non-contributory basis, plus a waiver of subrogation. These do not change with the dollar amount but they do change whether the coverage actually protects you.',
        ],
      },
      {
        heading: 'How VendorSuite enforces tiered requirements',
        paragraphs: [
          'VendorSuite\'s requirement engine is built for exactly this. You define coverage minimums and required documents once per vendor type, override them per property, and add per-vendor exceptions where needed. Every certificate is then scored against the specific requirements for the properties that vendor actually serves—so a vendor can be compliant at your duplex and non-compliant at your high-rise, correctly and automatically.',
        ],
      },
    ],
  },
];

export const getGuide = (slug) => GUIDES.find((g) => g.slug === slug);
